import * as React from 'react';

interface IAssistantTurnStatusContextValue {
  /**
   * Whether the assistant is currently working a turn — true from the
   * optimistic send until the terminal frame. Published by `AssistantPane`
   * (the WebSocket's single mount site) and reset to false when it unmounts.
   *
   * A false read while the pane is unmounted is safe by construction: the
   * pane owns the socket, so no pane means no connection and no client-side
   * turn. If the socket ever moves out of the pane, this flag must move with
   * it or mid-turn alert blocking silently stops working.
   */
  isTurnInFlight: boolean;
  setTurnInFlight: (isInFlight: boolean) => void;
}

/**
 * Default reflects "no assistant running", so surfaces rendered outside the
 * provider — Storybook, tests — treat every alert row as clickable.
 */
const DEFAULT_VALUE: IAssistantTurnStatusContextValue = {
  isTurnInFlight: false,
  setTurnInFlight: () => {},
};

const AssistantTurnStatusContext = React.createContext<IAssistantTurnStatusContextValue>(DEFAULT_VALUE);

interface IAssistantTurnStatusProviderProps {
  children: React.ReactNode;
}

/**
 * Carries the assistant's turn-in-flight flag across the surfaces that refuse
 * alert clicks mid-turn. The Alerts Center is `AssistantPane`'s sibling in the
 * `xl+` column and a separate route below it, so it cannot read the pane's
 * `isProcessing` directly — the flag crosses that seam here.
 *
 * Deliberately its own context rather than a field on `SidePanelProvider`:
 * that provider feeds layout consumers (`useContentColumnsBreakpoint`), and a
 * flag that flips on every turn would re-render them for a value they never
 * read.
 */
const AssistantTurnStatusProvider = ({ children }: IAssistantTurnStatusProviderProps) => {
  const [isTurnInFlight, setTurnInFlight] = React.useState(false);

  const value = React.useMemo<IAssistantTurnStatusContextValue>(
    () => ({ isTurnInFlight, setTurnInFlight }),
    [isTurnInFlight]
  );

  return <AssistantTurnStatusContext.Provider value={value}>{children}</AssistantTurnStatusContext.Provider>;
};

export default AssistantTurnStatusProvider;

/**
 * Reads the assistant turn status. `AssistantPane` writes it; the alert
 * surfaces (`AssistantAlertCarousel`, `AlertsCenter`) read `isTurnInFlight` to
 * refuse clicks mid-turn. Outside the provider it returns the not-in-flight
 * default, so rows stay clickable in Storybook.
 *
 * @returns `isTurnInFlight` — whether the assistant is working a turn — and
 *   `setTurnInFlight`, the stable setter the pane publishes through (a no-op
 *   outside the provider).
 */
export const useAssistantTurnStatus = (): IAssistantTurnStatusContextValue =>
  React.useContext(AssistantTurnStatusContext);
